import { useSvgBbox, useSvgTextSize } from '@festina-life/ui/hooks'
import { Group } from '@visx/group'
import { type HierarchyNode, Tree } from '@visx/hierarchy'

import { flattenD3TreeNode } from '../modelHierarchy/d3Helpers'
import {
  HORIZONTAL_NODE_INDENTATION,
  STROKE_WIDTH,
  TEXT_BOX_PADDING_X,
  TEXT_BOX_PADDING_Y,
} from './constants'
import { SubTemplateTreeLink } from './SubTemplateTreeLink'
import { SubTemplateTreeNode } from './SubTemplateTreeNode'
import type { TreeNode } from './types'

type Props = {
  /** Node hierarchy type to render. */
  d3Hierarchy: HierarchyNode<TreeNode>
}

export const SubTemplateDiagram = ({ d3Hierarchy }: Props) => {
  const {
    ref,
    width: bboxWidth,
    height: bboxHeight,
  } = useSvgBbox({
    x: STROKE_WIDTH,
    y: STROKE_WIDTH * 1.5, // half at the top, full width at the bottom
  })
  const { dimensions: currentSvgTextDimensions, svgRef } =
    useSvgTextSize<SVGGElement>({})

  const sizeHeight = bboxWidth
  const sizeWidth = bboxHeight

  const nodeHeightWithoutSpacing =
    currentSvgTextDimensions.height + 2 * TEXT_BOX_PADDING_Y
  const nodeHeightWithPadding = nodeHeightWithoutSpacing + TEXT_BOX_PADDING_X

  return (
    <svg width={bboxWidth} height={bboxHeight} ref={ref}>
      <Group top={STROKE_WIDTH / 2} left={STROKE_WIDTH / 2} innerRef={svgRef}>
        <Tree
          key={d3Hierarchy.data.id}
          root={d3Hierarchy}
          size={[sizeWidth, sizeHeight]}
        >
          {(tree) => {
            flattenD3TreeNode({
              node: tree,
              nodeHeightWithPadding,
              indentation: HORIZONTAL_NODE_INDENTATION,
            })

            return (
              <Group>
                {tree.links().map((link, index) => (
                  <SubTemplateTreeLink
                    /* oxlint-disable-next-line react/no-array-index-key */
                    key={`${link.source.id ?? ''}-${link.target.id ?? ''}-${index}`}
                    link={link}
                    lineClassName="fill-transparent stroke-1 stroke-subtemplateLine"
                    sourceConnectorOffset={{
                      dy: nodeHeightWithoutSpacing,
                      dx: TEXT_BOX_PADDING_X,
                    }}
                    targetConnectorOffset={{
                      dy: nodeHeightWithoutSpacing / 2,
                      dx: 0,
                    }}
                  />
                ))}
                {tree.descendants().map((node, index) => (
                  <SubTemplateTreeNode
                    // oxlint-disable-next-line react/no-array-index-key
                    key={`${node.data.id}-${index}`}
                    node={node}
                  />
                ))}
              </Group>
            )
          }}
        </Tree>
      </Group>
    </svg>
  )
}
